import { useEffect, useMemo, useRef, useState, type ComponentProps, type ReactElement } from "react";
import { useForm, type ControllerProps, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@workspace/ui/components/ui/form";
import { Input } from "@workspace/ui/components/ui/input";
import { Textarea } from "@workspace/ui/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@workspace/ui/components/ui/select";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@/hooks/useUser";
import { useLocalDraft, writeLocalDraft } from "@/hooks/useLocalDraft";
import { useGeoHint } from "@/hooks/useCars";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { marketToCountry } from "@/i18n/markets";
import { Sentry } from "@/lib/sentry";
import { track } from "@/lib/analytics";
import { AdminDatePicker } from "@/components/ui/admin-date-primitives";
import { getPhoneExample } from "./phone-example";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

export const CONCIERGE_PURPOSES = [
  "wedding",
  "celebration_gift",
  "driving_trip",
  "film_tv",
  "photoshoot_advertising",
  "corporate",
  "other",
] as const;

export type ConciergePurpose = (typeof CONCIERGE_PURPOSES)[number];
export type ConciergeFormVariant = "consumer" | "professional";
export type ConciergeFormSource = "hero" | "inline" | "page";

export interface ConciergePrefill {
  lookingFor?: string;
  desiredDate?: string;
  dateFrom?: string;
  dateTo?: string;
  location?: string;
  resultCount?: number;
}

type ConciergeFormValues = {
  fullName: string;
  workEmail: string;
  phone: string;
  purpose: ConciergePurpose | "";
  desiredDate: string;
  eventDate: string;
  location: string;
  lookingFor: string;
  notes: string;
  primaryUseCases: string;
  fleetSize: string;
  regionsServed: string;
  budgetRange: string;
  message: string;
};

type ConciergeErrorBody = {
  code?: string;
  error?: string;
  retryAfter?: number;
};

export function getConciergeErrorMessage(
  status: number,
  body: ConciergeErrorBody | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): { message: string; expected: boolean } {
  if (status === 400 || body?.code === "validation_error") {
    return { message: t("concierge.errors.validation"), expected: true };
  }
  if (status === 429) {
    const seconds = Math.max(1, Number(body?.retryAfter) || 60);
    return { message: t("concierge.errors.rate_limited", { seconds }), expected: true };
  }
  return { message: t("concierge.errors.submit_failed_desc"), expected: false };
}

const LEGACY_EVENT_TYPE_BY_PURPOSE: Record<ConciergePurpose, string> = {
  wedding: "wedding",
  celebration_gift: "other",
  driving_trip: "tour",
  film_tv: "film_tv",
  photoshoot_advertising: "brand_activation",
  corporate: "corporate",
  other: "other",
};

const PURPOSE_BY_LEGACY_EVENT_TYPE: Record<string, ConciergePurpose> = {
  wedding: "wedding",
  film_tv: "film_tv",
  corporate: "corporate",
  brand_activation: "photoshoot_advertising",
  tour: "driving_trip",
  other: "other",
};

const FLEET_SIZE_OPTIONS: { key: string; value: number }[] = [
  { key: "single", value: 1 },
  { key: "small", value: 3 },
  { key: "medium", value: 8 },
  { key: "large", value: 20 },
  { key: "fleet", value: 50 },
];
const BUDGET_KEYS = ["under_5k", "5k_15k", "15k_50k", "50k_150k", "150k_plus", "not_sure"] as const;

function legacyPurpose(value: unknown): ConciergePurpose | "" {
  if (typeof value !== "string") return "";
  if ((CONCIERGE_PURPOSES as readonly string[]).includes(value)) return value as ConciergePurpose;
  return PURPOSE_BY_LEGACY_EVENT_TYPE[value] || "";
}

function isUuid(value: unknown): value is string {
  return typeof value === "string"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function createRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === "x" ? random : (random & 0x3) | 0x8).toString(16);
  });
}

export interface ConciergeRequestFormProps {
  variant: ConciergeFormVariant;
  source: ConciergeFormSource;
  prefill?: ConciergePrefill;
  active?: boolean;
  onClose?: () => void;
}

export function ConciergeRequestForm({
  variant,
  source,
  prefill,
  active = true,
  onClose,
}: ConciergeRequestFormProps) {
  const { t, i18n } = useAppTranslation();
  const { toast } = useToast();
  const { profile, session, user, isLoading: authLoading } = useUser();
  const { data: geoHint } = useGeoHint();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [requestMeta, setRequestMeta] = useState({ requestId: "", requestFingerprint: "" });
  const submitInFlight = useRef(false);
  const proDraftRestored = useRef(false);
  const isProfessional = variant === "professional";
  const authOwnerId = user?.id || session?.user?.id || null;
  const lastAuthOwnerIdRef = useRef(authOwnerId);
  const settledAnonymousSessionRef = useRef(false);
  const isOwnerLossOrSwitch = !!lastAuthOwnerIdRef.current && lastAuthOwnerIdRef.current !== authOwnerId;

  const formSchema = useMemo(
    () =>
      z.object({
        fullName: z
          .string()
          .min(2, t(isProfessional ? "professional.validation.name_min" : "concierge.validation.name_min"))
          .max(120),
        workEmail: z
          .string()
          .min(1, t(isProfessional ? "professional.validation.email_required" : "concierge.validation.email_required"))
          .email(t(isProfessional ? "professional.validation.email_invalid" : "concierge.validation.email_invalid"))
          .max(200),
        phone: z.string().max(40).optional().or(z.literal("")),
        purpose: z.enum(CONCIERGE_PURPOSES, {
          message: t(isProfessional ? "professional.validation.event_type_required" : "concierge.validation.purpose_required"),
        }),
        desiredDate: z.string().optional(),
        eventDate: z.string().max(120).optional().or(z.literal("")),
        location: isProfessional
          ? z.string().min(2, t("professional.validation.location_required")).max(300)
          : z.string().max(200).optional().or(z.literal("")),
        lookingFor: isProfessional
          ? z.string().max(500).optional().or(z.literal(""))
          : z.string().min(2, t("concierge.validation.looking_for_required")).max(500),
        notes: z.string().max(2000).optional().or(z.literal("")),
        primaryUseCases: z.string().max(500).optional().or(z.literal("")),
        fleetSize: z.string().max(20).optional().or(z.literal("")),
        regionsServed: z.string().max(300).optional().or(z.literal("")),
        budgetRange: z.string().max(80).optional().or(z.literal("")),
        message: z.string().max(4000).optional().or(z.literal("")),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, isProfessional],
  );

  type ValidatedValues = z.infer<typeof formSchema>;
  const form = useForm<ValidatedValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      workEmail: "",
      phone: "",
      purpose: "" as unknown as ConciergePurpose,
      desiredDate: "",
      eventDate: "",
      location: "",
      lookingFor: "",
      notes: "",
      primaryUseCases: "",
      fleetSize: "",
      regionsServed: "",
      budgetRange: "",
      message: "",
    },
  });
  const { isDirty: formIsDirty, dirtyFields: formDirtyFields } = form.formState;
  const TypedFormField = FormField as <TName extends FieldPath<ValidatedValues>>(
    props: ControllerProps<ValidatedValues, TName>,
  ) => ReactElement;

  const profileFullName = useMemo(
    () => [profile?.first_name, profile?.last_name].map((part) => (part || "").trim()).filter(Boolean).join(" "),
    [profile?.first_name, profile?.last_name],
  );
  const profilePhone = useMemo(() => {
    const fromUser = (user?.phone || "").trim();
    if (fromUser) return fromUser;
    const meta = user?.user_metadata as Record<string, unknown> | undefined;
    return typeof meta?.phone === "string" ? meta.phone.trim() : "";
  }, [user?.phone, user?.user_metadata]);
  const composedLookingFor = useMemo(() => {
    const base = (prefill?.lookingFor || "").trim();
    const parts: string[] = [];
    if (prefill?.location?.trim()) {
      parts.push(t("concierge.prefill.in_location", { location: prefill.location.trim() }));
    }
    if (prefill?.dateFrom && prefill?.dateTo) {
      parts.push(t("concierge.prefill.between_dates", { from: prefill.dateFrom, to: prefill.dateTo }));
    } else if (prefill?.dateFrom) {
      parts.push(t("concierge.prefill.from_date", { from: prefill.dateFrom }));
    }
    if (base && parts.length) return `${base}: ${parts.join(" · ")}`;
    if (base) return base;
    return parts.length ? t("concierge.prefill.searching_seed", { context: parts.join(" · ") }) : "";
  }, [prefill?.lookingFor, prefill?.location, prefill?.dateFrom, prefill?.dateTo, t]);
  const defaultValues = useMemo<ConciergeFormValues>(
    () => ({
      fullName: profileFullName,
      workEmail: profile?.email || user?.email || "",
      phone: profilePhone,
      purpose: "",
      desiredDate: prefill?.desiredDate || prefill?.dateFrom || "",
      eventDate: "",
      location: prefill?.location || "",
      lookingFor: composedLookingFor,
      notes: "",
      primaryUseCases: "",
      fleetSize: "",
      regionsServed: "",
      budgetRange: "",
      message: "",
    }),
    [profileFullName, profile?.email, user?.email, profilePhone, prefill?.desiredDate, prefill?.dateFrom, prefill?.location, composedLookingFor],
  );

  const watched = form.watch();
  const draftValues = useMemo(() => {
    const current = {
      fullName: watched.fullName || "",
      workEmail: watched.workEmail || "",
      phone: watched.phone || "",
      purpose: watched.purpose || "",
      desiredDate: watched.desiredDate || "",
      eventDate: watched.eventDate || "",
      location: watched.location || "",
      lookingFor: watched.lookingFor || "",
      notes: watched.notes || "",
      primaryUseCases: watched.primaryUseCases || "",
      fleetSize: watched.fleetSize || "",
      regionsServed: watched.regionsServed || "",
      budgetRange: watched.budgetRange || "",
      message: watched.message || "",
      ownerAuthId: user?.id || session?.user?.id || "anonymous",
    };
    return isProfessional
      ? {
          ...current,
          eventType: LEGACY_EVENT_TYPE_BY_PURPOSE[current.purpose as ConciergePurpose] || current.purpose,
          regionsServed: current.location,
          ...requestMeta,
        }
      : { ...current, ...requestMeta };
  }, [
    isProfessional, requestMeta, user?.id, session?.user?.id, watched.fullName, watched.workEmail, watched.phone, watched.purpose,
    watched.desiredDate, watched.eventDate, watched.location, watched.lookingFor, watched.notes,
    watched.primaryUseCases, watched.fleetSize, watched.regionsServed, watched.budgetRange, watched.message,
  ]);
  const { readDraft, clearDraft } = useLocalDraft({
    key: isProfessional ? "professional-inquiry" : "concierge-request",
    version: 1,
    values: draftValues,
    enabled: active && !submitted && !authLoading,
    flushOnSessionEnd: !isOwnerLossOrSwitch,
    isEmpty: (values) => isProfessional
      ? !String(values.message || "").trim()
        && !String(values.primaryUseCases || "").trim()
        && !String(values.location || values.regionsServed || "").trim()
        && !String(values.fullName || "").trim()
      : !String(values.notes || "").trim()
        && !String(values.lookingFor || "").trim()
        && !String(values.location || "").trim()
        && !String(values.desiredDate || "").trim(),
  });
  const clearDraftRef = useRef(clearDraft);
  clearDraftRef.current = clearDraft;
  useEffect(() => {
    if (submitted) clearDraftRef.current();
  }, [submitted]);

  useEffect(() => {
    if (!active || authLoading) return;
    const previousOwner = lastAuthOwnerIdRef.current;
    const ownerChanged = previousOwner !== authOwnerId;
    const canAdoptAnonymousDraft = !previousOwner && !!authOwnerId && settledAnonymousSessionRef.current;
    if (isProfessional && proDraftRestored.current && !ownerChanged) return;
    const storedDraft = readDraft() as Record<string, unknown> | null;
    const hasSavedOwner = storedDraft
      ? Object.prototype.hasOwnProperty.call(storedDraft, "ownerAuthId")
      : false;
    const savedOwner = storedDraft?.ownerAuthId;
    const ownerMatches = !storedDraft
      || (hasSavedOwner
        ? savedOwner === authOwnerId || (savedOwner === "anonymous" && (!authOwnerId || canAdoptAnonymousDraft))
        : !authOwnerId || canAdoptAnonymousDraft);
    const mustClearPreviousOwner = ownerChanged && !!previousOwner && !authOwnerId;
    if ((storedDraft && !ownerMatches) || mustClearPreviousOwner) clearDraft();
    const draft = ownerMatches ? storedDraft : null;
    const draftPurpose = legacyPurpose(draft?.purpose) || legacyPurpose(draft?.eventType);
    setRequestMeta({
      requestId: isUuid(draft?.requestId) ? draft.requestId : "",
      requestFingerprint: typeof draft?.requestFingerprint === "string" ? draft.requestFingerprint : "",
    });
    const merged: ConciergeFormValues = {
      ...defaultValues,
      fullName: defaultValues.fullName || String(draft?.fullName || ""),
      workEmail: defaultValues.workEmail || String(draft?.workEmail || ""),
      phone: defaultValues.phone || String(draft?.phone || ""),
      purpose: defaultValues.purpose || draftPurpose,
      desiredDate: defaultValues.desiredDate || String(draft?.desiredDate || ""),
      eventDate: String(draft?.eventDate || ""),
      location: defaultValues.location || String(draft?.location || draft?.regionsServed || ""),
      lookingFor: defaultValues.lookingFor || String(draft?.lookingFor || ""),
      notes: String(draft?.notes || ""),
      primaryUseCases: String(draft?.primaryUseCases || ""),
      fleetSize: String(draft?.fleetSize || ""),
      regionsServed: String(draft?.regionsServed || draft?.location || ""),
      budgetRange: String(draft?.budgetRange || ""),
      message: String(draft?.message || ""),
    };
    if (formIsDirty && (!ownerChanged || !previousOwner)) {
      const currentValues = form.getValues();
      for (const field of Object.keys(formDirtyFields) as (keyof ConciergeFormValues)[]) {
        if (formDirtyFields[field]) {
          if (field === "purpose" && !currentValues.purpose && merged.purpose) continue;
          (merged as unknown as Record<string, unknown>)[field] = currentValues[field];
        }
      }
    }
    form.reset(merged as unknown as ValidatedValues);
    setSubmitted(false);
    setSubmitError(null);
    if (isProfessional) proDraftRestored.current = true;
    lastAuthOwnerIdRef.current = authOwnerId;
    settledAnonymousSessionRef.current = !authOwnerId;
    // Draft access is intentionally caller-driven only when a surface opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, authLoading, authOwnerId, defaultValues, form, isProfessional]);

  const getPhonePlaceholder = () => {
    const pathnameMarket = typeof window !== "undefined"
      ? window.location.pathname.split("/").filter(Boolean).find((segment) => marketToCountry(segment))
      : undefined;
    return getPhoneExample({
      explicitMarket: pathnameMarket,
      visitorCountry: geoHint?.country,
      locale: i18n.language,
      browserLocale: typeof navigator !== "undefined" ? navigator.language : undefined,
    });
  };

  const onSubmit = async (values: ValidatedValues) => {
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setSubmitError(null);
    const desiredDateIso = /^\d{4}-\d{2}-\d{2}$/.test(values.desiredDate || "")
      ? values.desiredDate
      : undefined;
    const eventDateIso = /^\d{4}-\d{2}-\d{2}$/.test(values.eventDate || "")
      ? values.eventDate
      : undefined;
    const locale = i18n.language || undefined;

    try {
      const endpoint = isProfessional
        ? `${API_BASE}/marketing/professional-leads`
        : `${API_BASE}/marketing/concierge-leads`;
      const payload = isProfessional
        ? {
            fullName: values.fullName,
            workEmail: values.workEmail,
            phone: values.phone || undefined,
            purpose: values.purpose,
            eventType: LEGACY_EVENT_TYPE_BY_PURPOSE[values.purpose],
            eventDate: eventDateIso,
            eventDateText: eventDateIso ? undefined : values.eventDate || undefined,
            city: values.location,
            vehicleCount: FLEET_SIZE_OPTIONS.find((option) => option.key === values.fleetSize)?.value,
            preferredStyles: values.primaryUseCases || undefined,
            budgetRange: values.budgetRange || undefined,
            message: values.message || undefined,
            primaryUseCases: values.primaryUseCases || undefined,
            fleetSize: values.fleetSize || undefined,
            fleetSizeLabel: values.fleetSize ? t(`professional.fleet_size.${values.fleetSize}`) : undefined,
            regionsServed: values.location,
            source: "professional",
            locale,
            sourcePath: typeof window !== "undefined" ? window.location.pathname : "/professional",
          }
        : {
            fullName: values.fullName,
            workEmail: values.workEmail,
            phone: values.phone || undefined,
            purpose: values.purpose,
            lookingFor: values.lookingFor,
            desiredDate: desiredDateIso,
            location: values.location || undefined,
            notes: values.notes || undefined,
            source,
            resultCount: prefill?.resultCount,
            locale,
            sourcePath: typeof window !== "undefined" ? window.location.pathname : "/search",
          };
      const requestFingerprint = JSON.stringify(payload);
      const requestId = requestMeta.requestId && requestMeta.requestFingerprint === requestFingerprint
        ? requestMeta.requestId
        : createRequestId();
      const nextMeta = { requestId, requestFingerprint };
      setRequestMeta(nextMeta);
      const draftSnapshot = {
        ...draftValues,
        ...values,
        purpose: values.purpose,
        eventType: isProfessional ? LEGACY_EVENT_TYPE_BY_PURPOSE[values.purpose] : undefined,
        regionsServed: isProfessional ? values.location : undefined,
        ownerAuthId: user?.id || session?.user?.id || "anonymous",
        requestId,
        requestFingerprint,
      };
      writeLocalDraft(isProfessional ? "professional-inquiry" : "concierge-request", 1, draftSnapshot);
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ ...payload, requestId }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ConciergeErrorBody | null;
        const mapped = getConciergeErrorMessage(res.status, body, t);
        if (!mapped.expected) {
          Sentry.captureMessage("Concierge request submission failed", {
            level: "error",
            tags: { status: String(res.status), code: body?.code || "unknown" },
          });
        }
        setSubmitError(mapped.message);
        toast({
          title: t("concierge.errors.submit_failed_title"),
          description: mapped.message,
          variant: "destructive",
        });
        return;
      }

      const json = (await res.json().catch(() => null)) as { leadId?: string | number } | null;
      if (!isProfessional) {
        track("concierge_request_submitted", {
          source,
          result_count: prefill?.resultCount,
          location: values.location || null,
          desired_date: values.desiredDate || values.eventDate || null,
          lead_id: json?.leadId ?? null,
          locale,
        });
      }
      setSubmitted(true);
    } catch (err) {
      Sentry.captureException(err);
      const message = t("concierge.errors.submit_failed_desc");
      setSubmitError(message);
      toast({
        title: t("concierge.errors.submit_failed_title"),
        description: message,
        variant: "destructive",
      });
    } finally {
      submitInFlight.current = false;
    }
  };

  const fieldLabel = (consumerKey: string, professionalKey: string) =>
    t(isProfessional ? professionalKey : consumerKey);
  const phonePlaceholder = getPhonePlaceholder();

  if (submitted) {
    return (
      <div
        className="flex flex-col items-center py-8 text-center"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid={`concierge-${variant}-success`}
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <CheckCircle2 className="h-7 w-7" aria-hidden />
        </div>
        <h3 className="mt-4 text-xl font-bold text-foreground">
          {t(isProfessional ? "professional.inquiry.success.title" : "concierge.success.title")}
        </h3>
        <p className="mt-2 max-w-lg text-sm text-muted-foreground">{t("concierge.response_promise")}</p>
        {onClose ? (
          <Button type="button" className="mt-6" onClick={onClose}>
            {t("concierge.success.close_cta")}
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <Form {...(form as unknown as ComponentProps<typeof Form>)}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={isProfessional ? "space-y-6" : "space-y-4"}
        data-testid={isProfessional ? "professional-inquiry-form" : "concierge-request-form"}
      >
        <div role="alert" aria-live="assertive" aria-atomic="true" className="sr-only">{submitError ?? ""}</div>
        {submitError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" data-testid="concierge-submit-error">
            {submitError}
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TypedFormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{fieldLabel("concierge.fields.name_label", "professional.inquiry.fields.full_name")}</FormLabel>
                <FormControl>
                  <Input autoComplete="name" placeholder={fieldLabel("concierge.fields.name_placeholder", "professional.inquiry.fields.full_name_placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <TypedFormField
            control={form.control}
            name="workEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{fieldLabel("concierge.fields.email_label", "professional.inquiry.fields.work_email")}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="email" placeholder={fieldLabel("concierge.fields.email_placeholder", "professional.inquiry.fields.work_email_placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <TypedFormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{fieldLabel("concierge.fields.phone_label", "professional.inquiry.fields.phone")}</FormLabel>
                <FormControl>
                  <Input type="tel" autoComplete="tel" placeholder={phonePlaceholder || fieldLabel("concierge.fields.phone_placeholder", "professional.inquiry.fields.phone_placeholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <TypedFormField
            control={form.control}
            name="purpose"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{fieldLabel("concierge.fields.purpose_label", "professional.inquiry.fields.event_type")}</FormLabel>
                <Select
                  onValueChange={(value) => {
                    if ((CONCIERGE_PURPOSES as readonly string[]).includes(value)) {
                      field.onChange(value as ConciergePurpose);
                    }
                  }}
                  value={field.value ?? ""}
                >
                  <FormControl>
                    <SelectTrigger data-testid="select-concierge-purpose">
                      <SelectValue placeholder={fieldLabel("concierge.fields.purpose_placeholder", "professional.inquiry.fields.choose")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CONCIERGE_PURPOSES.map((purpose) => (
                      <SelectItem key={purpose} value={purpose}>{t(`concierge.purposes.${purpose}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {isProfessional ? (
            <>
              <TypedFormField
                control={form.control}
                name="eventDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("professional.inquiry.fields.event_date")}</FormLabel>
                    <FormControl><Input placeholder={t("professional.inquiry.fields.event_date_placeholder")} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TypedFormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("professional.inquiry.fields.regions")}</FormLabel>
                    <FormControl><Input placeholder={t("professional.inquiry.fields.regions_placeholder")} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TypedFormField
                control={form.control}
                name="fleetSize"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("professional.inquiry.fields.fleet_size")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? ""}>
                      <FormControl><SelectTrigger><SelectValue placeholder={t("professional.inquiry.fields.choose")} /></SelectTrigger></FormControl>
                      <SelectContent>
                        {FLEET_SIZE_OPTIONS.map((option) => (
                          <SelectItem key={option.key} value={option.key}>{t(`professional.fleet_size.${option.key}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TypedFormField
                control={form.control}
                name="budgetRange"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("professional.inquiry.fields.budget")}</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value ?? ""}>
                      <FormControl><SelectTrigger><SelectValue placeholder={t("professional.inquiry.fields.choose")} /></SelectTrigger></FormControl>
                      <SelectContent>
                        {BUDGET_KEYS.map((key) => {
                          const label = t(`professional.budget.${key}`);
                          return <SelectItem key={key} value={label}>{label}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          ) : (
            <>
              <TypedFormField
                control={form.control}
                name="desiredDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("concierge.fields.date_label")}</FormLabel>
                    <FormControl>
                      <AdminDatePicker label={t("concierge.fields.date_label")} value={field.value || ""} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <TypedFormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("concierge.fields.location_label")}</FormLabel>
                    <FormControl><Input placeholder={t("concierge.fields.location_placeholder")} {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
        </div>

        {isProfessional ? (
          <>
            <TypedFormField
              control={form.control}
              name="primaryUseCases"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("professional.inquiry.fields.use_cases")}</FormLabel>
                  <FormControl><Input placeholder={t("professional.inquiry.fields.use_cases_placeholder")} {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <TypedFormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("professional.inquiry.fields.message")}</FormLabel>
                  <FormControl><Textarea rows={4} placeholder={t("professional.inquiry.fields.message_placeholder")} {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : (
          <>
            <TypedFormField
              control={form.control}
              name="lookingFor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("concierge.fields.looking_for_label")}</FormLabel>
                  <FormControl><Textarea rows={3} placeholder={t("concierge.fields.looking_for_placeholder")} {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <TypedFormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("concierge.fields.notes_label")}</FormLabel>
                  <FormControl><Textarea rows={3} placeholder={t("concierge.fields.notes_placeholder")} {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {fieldLabel("concierge.modal.privacy_note", "professional.inquiry.privacy")}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {onClose ? (
              <Button type="button" variant="ghost" onClick={onClose} disabled={form.formState.isSubmitting}>
                {t("concierge.modal.cancel")}
              </Button>
            ) : null}
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className={isProfessional ? "rounded-xl bg-brand px-8 py-6 text-base font-bold text-white hover:bg-brand-light" : "bg-primary text-primary-foreground hover:bg-primary/90"}
              data-testid="submit-concierge-request"
            >
              {form.formState.isSubmitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />{t("concierge.modal.submitting")}</>
              ) : t("concierge.submit_cta")}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}

export default ConciergeRequestForm;